import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Typography from '@mui/material/Typography';
import Button from 'components/Button';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import {
  getQuestions,
  saveConnectCard,
  saveQuestion,
  getCardTemplates,
} from 'services/connect-card.service';
import Checkbox from 'components/form/Checkbox';
import Spin from 'components/Spin';
import {
  FIELD_ICONS,
  COLORS,
} from 'modules/connect-card/constants/field-types';
import { snack } from 'utils/toast';
import pick from 'lodash/pick';
import useQueryParams from 'hooks/useQueryParams';
import { useSelector } from 'react-redux';
import Card from '@mui/material/Card';

const steps = [1, 2];

const isDefaultQuestion = (q) => {
  return (
    q.is_first_name === '1' || q.is_email_address === '1' || q.is_mobile === '1'
  );
};

const CardsList = ({ onChange, value }) => {
  const [cards, setCards] = useState();
  const [cat, setCat] = useState('all');

  useEffect(() => {
    getCardTemplates().then(setCards);
  }, []);

  const categories = useMemo(() => {
    if (!cards) return [];
    return cards.reduce(
      (acc, card) => {
        card.categories.forEach((c) => {
          if (!acc.some((a) => a.hash === c.hash)) {
            acc.push(c);
          }
        });
        return acc;
      },
      [{ hash: 'all', category_name: 'All' }],
    );
  }, [cards]);

  if (!cards) {
    return <Spin spinning />;
  }

  const filteredCards =
    cat === 'all'
      ? cards
      : cards.filter((c) => c.categories.some((i) => i.hash === cat));

  return (
    <>
      <Stack mb={1.5} direction="row" spacing={1}>
        {categories.map((c) => (
          <Chip
            icon={cat === c.hash && <CheckCircleOutlineIcon fontSize="small" />}
            color={cat === c.hash ? 'primary' : 'default'}
            variant="outlined"
            key={c.hash}
            label={c.category_name}
            onClick={() => setCat(c.hash)}
          />
        ))}
      </Stack>
      <Grid container spacing={2}>
        {filteredCards.map((card) => (
          <Grid item xs={12} sm={6} md={6} key={card.id}>
            <Paper
              sx={{
                p: 1,
                cursor: 'pointer',
                borderColor: value?.id === card.id && 'primary.main',
                bgcolor: value?.id === card.id && 'primary.lighter',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
              }}
              variant="outlined"
              onClick={() => onChange(card)}
            >
              <Typography fontWeight={600} variant="subtitle1">
                {card.card_name}
              </Typography>
              <Typography gutterBottom color="text.secondary">
                {card.question_count} questions
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </>
  );
};

const QuestionsList = ({ onChange, value, card }) => {
  const [questions, setQuestions] = useState();

  useEffect(() => {
    getQuestions(card.hash).then((questions) => {
      setQuestions(questions);
      onChange(questions.filter((i) => isDefaultQuestion(i)));
    });
  }, []);

  const onCheck = (question) => (e) => {
    if (e.target.checked) {
      onChange([...value, question]);
    } else {
      onChange(value.filter((q) => q.id !== question.id));
    }
  };

  const onCheckAll = (e) => {
    onChange(
      e.target.checked
        ? questions
        : questions.filter((i) => isDefaultQuestion(i)),
    );
  };

  if (!questions) {
    return <Spin spinning />;
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle1" fontWeight={600}>
          First Time Follow Up
        </Typography>
        <Typography color="text.secondary" sx={{ flex: 1 }}>
          {questions.length} questions
        </Typography>
        <Checkbox
          sx={{ pr: 1.5 }}
          onChange={onCheckAll}
          checked={questions.length === value.length}
          indeterminate={value.length > 0 && value.length < questions.length}
          labelPlacement="start"
          label="Select all"
        />
      </Stack>
      <Stack spacing={1}>
        {questions.map((question, idx) => {
          const Icon = FIELD_ICONS[question.question_type];
          const color = COLORS[question.question_type];
          const isDisabled = isDefaultQuestion(question);
          return (
            <Stack
              variant="outlined"
              selected={value.some((i) => i.id === question.id)}
              component={Card}
              key={question.id}
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                pl: 1,
              }}
            >
              <Box
                backgroundColor={color.color}
                borderRadius="50%"
                width="20px"
                height="20px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon
                  sx={{
                    color: 'white',
                    fontSize: 12,
                  }}
                />
              </Box>
              <Typography sx={{ flex: 1 }}>
                {idx + 1}. {question.question_label}
              </Typography>
              <Tooltip title={isDisabled && 'This question cannot be removed'}>
                <span>
                  <Checkbox
                    disabled={isDisabled}
                    onChange={onCheck(question)}
                    checked={
                      isDisabled
                        ? true
                        : value.some((i) => i.id === question.id)
                    }
                  />
                </span>
              </Tooltip>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};

const TemplateModal = ({ onClose, onSubmit }) => {
  const [activeStep, setActiveStep] = React.useState(0);
  const [selected, setSelected] = useState('');
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const account = useSelector((state) => state.settings.account);
  const history = useHistory();
  const query = useQueryParams();

  const createFromTemplate = async () => {
    try {
      const newCard = pick(selected, [
        'card_background_color',
        'card_description',
        'card_headline',
        'card_name',
        'card_template',
        'card_text_color',
        'card_type',
        'logo_or_video',
        'logo_or_video_url',
      ]);
      newCard.group_id = query.group_id;
      newCard.is_replicating = 1;
      if (account.account_color) {
        newCard.card_background_color = '#' + account.account_color;
      }
      const card = await saveConnectCard(newCard);

      await Promise.all(
        selectedQuestions.map((question, idx) =>
          saveQuestion({
            ...pick(question, [
              'question_type',
              'question_possible_answers',
              'question_label',
              'question_required',
              'question_settings',
            ]),
            connect_card_id: card.id,
            sort_order: idx,
          }),
        ),
      );
      history.push(`/connect/cards/${card.hash}`);
    } catch (e) {
      snack.error(e.message);
    }
  };

  const handleStep = (step) => () => {
    setActiveStep(step);
  };

  const isLastStep = () => {
    return activeStep === steps.length - 1;
  };

  const onBack = () => {
    if (activeStep === 0) {
      onClose();
    } else {
      setActiveStep(activeStep - 1);
      setSelectedQuestions([]);
    }
  };

  const isStepComplete = () => {
    switch (activeStep) {
      case 0:
        return !!selected;
      default:
        return true;
    }
  };

  const onNext = () => {
    if (isLastStep()) {
      return createFromTemplate();
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  return (
    <Dialog open maxWidth="md">
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography gutterBottom variant="h2" fontWeight={600}>
            {activeStep === 0 ? 'Select a template' : 'Select questions'}
          </Typography>
        </Stack>

        {activeStep === 1 ? (
          <Typography color="text.secondary">
            Select which questions you would like to remove or include in your
            card.
          </Typography>
        ) : (
          <Typography color="text.secondary">
            Let's kick things off by creating your Connect Card with our
            templates. They're designed to help you get started quickly!
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Box pt={2}>
          {activeStep === 0 && (
            <CardsList onChange={setSelected} value={selected} />
          )}
          {activeStep === 1 && (
            <QuestionsList
              card={selected}
              onChange={setSelectedQuestions}
              value={selectedQuestions}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onBack} variant="text">
          Back
        </Button>
        <Button disabled={!isStepComplete()} onClick={onNext}>
          {isLastStep() ? 'Create' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateModal;
